import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Alert, Button, Card, Field, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { COUNTRIES } from '../platform/data';

interface Country {
  code: string;
  organizations: string[];
}
interface Holiday {
  id: string;
  date: string;
  name: string;
}
interface ImportResult {
  created: number;
  updated: number;
  total: number;
}

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

/** Feriados por país (guía 7.1): los carga el Administrador una vez al año, uno a uno o con un CSV. */
export function HolidaysSection() {
  const qc = useQueryClient();
  const countries = useQuery({ queryKey: ['parametros', 'feriados', 'paises'], queryFn: () => api<Country[]>('/api/parametros/feriados/paises') });
  const thisYear = new Date().getFullYear();
  const [country, setCountry] = useState<string | null>(null);
  const [year, setYear] = useState(thisYear);
  const code = country ?? countries.data?.[0]?.code ?? null;
  const key = ['parametros', 'feriados', code, year] as const;
  const holidays = useQuery({
    queryKey: key,
    queryFn: () => api<Holiday[]>(`/api/parametros/feriados?pais=${code}&anio=${year}`),
    enabled: !!code,
  });
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [confirming, setConfirming] = useState<Holiday | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Array<{ line: number; message: string }>>([]);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    setLineErrors([]);
    try {
      setNotice(await fn());
      await qc.invalidateQueries({ queryKey: ['parametros', 'feriados', code] });
    } catch (err) {
      setError(errorMessage(err));
      const body = err instanceof ApiError ? (err.body as { errors?: Array<{ line: number; message: string }> } | undefined) : undefined;
      if (body?.errors?.[0] && 'line' in body.errors[0]) setLineErrors(body.errors);
    } finally {
      setBusy(false);
    }
  }

  const add = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      await api('/api/parametros/feriados', { json: { countryCode: code, date, name: name.trim() } });
      setDate('');
      setName('');
      return t('fer.agregado');
    });
  };

  const importCsv = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite elegir el mismo archivo otra vez
    if (!file) return;
    void act(async () => {
      const r = await api<ImportResult>('/api/parametros/feriados/importar', { json: { countryCode: code, csv: await file.text() } });
      return t('fer.importado', { nuevos: r.created, actualizados: r.updated });
    });
  };

  if (!countries.data) return countries.error ? <Alert>{errorMessage(countries.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  if (countries.data.length === 0) return <Alert tone="info">{t('fer.sin_paises')}</Alert>;
  const current = countries.data.find((c) => c.code === code);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('fer.titulo')}</h2>
        <p className="max-w-2xl text-sm text-slate-600">{t('fer.descripcion')}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select id="fer-pais" label={t('fer.pais')} value={code ?? ''} onChange={(e) => setCountry(e.target.value)}>
            {countries.data.map((c) => (
              <option key={c.code} value={c.code}>
                {COUNTRIES.find((x) => x.code === c.code)?.name ?? c.code}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-32">
          <Select id="fer-anio" label={t('fer.anio')} value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {current && <p className="text-xs text-slate-500">{t('fer.aplica_a', { orgs: current.organizations.join(', ') })}</p>}
      {error && (
        <Alert>
          <p>{error}</p>
          {lineErrors.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {lineErrors.map((l) => (
                <li key={`${l.line}-${l.message}`}>{l.line > 0 ? t('fer.linea', { n: l.line, mensaje: l.message }) : l.message}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card>
        {!holidays.data ? (
          holidays.error ? <Alert>{errorMessage(holidays.error)}</Alert> : <Spinner label={t('comun.cargando')} />
        ) : holidays.data.length === 0 ? (
          <p className="text-sm text-slate-500">{t('fer.vacio', { anio: year })}</p>
        ) : (
          <ul className="divide-y divide-slate-100" aria-label={t('fer.titulo')}>
            {holidays.data.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 py-2">
                <span className="text-sm">
                  <span className="font-medium text-slate-900">{h.name}</span>
                  <span className="block text-xs capitalize text-slate-500">{formatDate(h.date)}</span>
                </span>
                <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.borrar')} ${h.name}`} onClick={() => setConfirming(h)}>
                  {t('plataforma.borrar')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {confirming && (
          <Alert tone="warning">
            <p>{t('fer.borrar_confirmar', { nombre: confirming.name })}</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api(`/api/parametros/feriados/${confirming.id}`, { method: 'DELETE' });
                    setConfirming(null);
                    return t('fer.borrado');
                  })
                }
              >
                {t('acceso.si_borrar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                {t('comun.cancelar')}
              </Button>
            </div>
          </Alert>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('fer.agregar')}>
          <form onSubmit={add} className="space-y-3">
            <Field id="fer-fecha" type="date" label={t('fer.fecha')} required value={date} onChange={(e) => setDate(e.target.value)} />
            <Field id="fer-nombre" label={t('fer.nombre')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            <Button type="submit" disabled={busy || !date || !name.trim()}>
              {t('plataforma.agregar')}
            </Button>
          </form>
        </Card>
        <Card title={t('fer.importar')}>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">{t('fer.importar_ayuda')}</p>
            <pre className="rounded bg-slate-50 p-2 text-xs">{'fecha;nombre\n01/01/2027;Año Nuevo\n2027-01-09;Día de los Mártires'}</pre>
            <label className="inline-block">
              <span className="sr-only">{t('fer.importar')}</span>
              <input type="file" accept=".csv,.txt,text/csv,text/plain" disabled={busy} onChange={importCsv} aria-label={t('fer.archivo')} className="text-sm" />
            </label>
          </div>
        </Card>
      </div>
    </div>
  );
}
